import * as React from 'react';
import { useIntl } from 'react-intl';

import { BodyCopy } from '@xing-com/typography';

import * as Styled from './progress-bar.styles';
import type { ProgressBarProps } from './progress-bar.types';

/** Avoid progress being lower than 0% or beyond 100% */
const clampProgress = (progress: number): number =>
  Math.min(Math.max(0, progress), 100);

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      className,
      labelText,
      progress,
      progressBarColor,
      showLabel = true,
      textPositioning = 'left',
      textVariant = 'default',
      variant = 'determinate',
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const intl = useIntl();
    const labelId = React.useId();
    const progressId = React.useId();
    const sanitizedProgress = progress && clampProgress(progress);

    /** Without a progress value the variant will be automatically 'indeterminate' */
    if (progress === undefined) {
      variant = 'indeterminate';
    }

    const accessibleLabel = React.useMemo(() => {
      if (variant === 'determinate' && sanitizedProgress !== undefined) {
        return labelText
          ? `${sanitizedProgress}% ${labelText}`
          : intl.formatMessage(
              {
                id: 'XDS_A11Y_PROGESS_BAR_COMPLETE',
                defaultMessage: '{percent} complete',
              },
              { percent: `${sanitizedProgress}%` }
            );
      }

      return (
        labelText ||
        intl.formatMessage({
          id: 'XDS_A11Y_LOADING',
          defaultMessage: 'Loading',
        })
      );
    }, [variant, sanitizedProgress, labelText, intl]);

    // Determine labeling strategy: use aria-labelledby only when we have a visible label
    const hasVisibleLabel = showLabel && variant === 'determinate';
    const useAriaLabelledBy = ariaLabelledBy || hasVisibleLabel;

    return (
      <Styled.Container
        className={className}
        data-xds="ProgressBar"
        ref={forwardedRef}
        {...props}
      >
        <Styled.Tracker
          aria-busy={variant === 'indeterminate'}
          aria-label={
            ariaLabel || (!useAriaLabelledBy ? accessibleLabel : undefined)
          }
          aria-labelledby={
            useAriaLabelledBy ? ariaLabelledBy || labelId : undefined
          }
          aria-valuemax={variant === 'determinate' ? 100 : undefined}
          aria-valuemin={variant === 'determinate' ? 0 : undefined}
          aria-valuenow={
            variant === 'determinate' ? sanitizedProgress : undefined
          }
          aria-valuetext={accessibleLabel}
          id={progressId}
          role="progressbar"
        >
          <Styled.ProgressIndicator
            $progress={sanitizedProgress}
            $progressBarColor={progressBarColor}
            $variant={variant}
            aria-hidden="true"
          />
        </Styled.Tracker>
        {variant === 'determinate' && showLabel && (
          <Styled.TextContainer
            $textPositioning={textPositioning}
            $textVariant={textVariant}
            aria-atomic="true"
            aria-live="polite"
            id={labelId}
          >
            <BodyCopy size="medium">
              {sanitizedProgress}%{labelText && ` ${labelText}`}
            </BodyCopy>
          </Styled.TextContainer>
        )}
      </Styled.Container>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';
